/**
 * Case history lines that say which checklist item (case round, finding 13).
 *
 * The case timeline's checklist lines read "Checklist item updated" with no
 * item: the event carries no label. The case's own checklist does carry
 * each item's created / updated / done times, so a history line written in
 * the same few seconds as an item's change names that item. Only the item's
 * LATEST change can be matched this way (the item keeps one updated time);
 * an older line keeps its plain wording.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorCaseTaskRead, DoorInvestigationEventRead } from '../../services/qcpInvestigations';

/** A history line and an item change this close together are the same write. */
const SAME_WRITE_MS = 5_000;

function near(a: string | null | undefined, b: string): boolean {
  if (!a) return false;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  return !Number.isNaN(ta) && !Number.isNaN(tb) && Math.abs(ta - tb) <= SAME_WRITE_MS;
}

/** An item name in quotes, cut to its first words when long. */
export function itemName(label: string, max = 70): string {
  const t = label.trim();
  if (t.length <= max) return `“${t}”`;
  const cut = t.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `“${(space > max / 2 ? cut.slice(0, space) : cut).trimEnd()}…”`;
}

/** The one task that matches; none when no task, or several, do (a batch
 *  of items written in the same second cannot be told apart). */
function only<T>(found: T[]): T | null {
  return found.length === 1 ? found[0] : null;
}

/** The history line's summary, naming the checklist item when it can. */
export function caseEventSummary(
  ev: Pick<DoorInvestigationEventRead, 'event_type' | 'summary' | 'created_at'>,
  tasks: ReadonlyArray<DoorCaseTaskRead>,
): string {
  if (ev.event_type === 'task_added') {
    const task = only(tasks.filter((t) => near(t.created_at, ev.created_at)));
    return task ? `${ev.summary}: ${itemName(task.label)}` : ev.summary;
  }
  if (ev.event_type === 'task_status_changed') {
    const task = only(
      tasks.filter((t) => near(t.updated_at, ev.created_at) || near(t.done_at, ev.created_at)),
    );
    if (!task) return ev.summary;
    const what =
      task.status === 'done' ? 'Ticked off' : task.status === 'cancelled' ? 'Removed' : 'Reopened';
    return `${what} ${itemName(task.label)}`;
  }
  if (ev.event_type === 'task_edited') {
    const task = only(tasks.filter((t) => near(t.updated_at, ev.created_at)));
    return task ? `Edited ${itemName(task.label)}` : ev.summary;
  }
  return ev.summary;
}
